import React, { forwardRef } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';
import { TextReg } from '../StyledText';

type TableCellProps = ViewProps & {
  children?: React.ReactNode;
  flex?: number;
  width?: number;
  align?: 'left' | 'center' | 'right';
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
};

/**
 * TableCell - Table body cell
 *
 * Use inside TableRow within TableBody.
 * Automatically styles text with regular weight.
 *
 * @example
 * ```tsx
 * <TableCell flex={2}>John Doe</TableCell>
 * <TableCell flex={1} align="center">
 *   <Badge>Active</Badge>
 * </TableCell>
 * <TableCell width={100} align="right">
 *   <Button size="small">Edit</Button>
 * </TableCell>
 * ```
 */
export const TableCell = forwardRef<View, TableCellProps>(
  (
    {
      children,
      style,
      flex,
      width,
      align = 'left',
      hideOnMobile,
      hideOnTablet,
      ...props
    },
    ref,
  ) => {
    const styles = useDynamicStyles(createStyles);

    const cellStyle = [
      styles.cell,
      flex !== undefined && { flex },
      width !== undefined && { width },
      align === 'center' && styles.centerAlign,
      align === 'right' && styles.rightAlign,
      hideOnMobile && styles.hideOnMobile,
      hideOnTablet && styles.hideOnTablet,
      style,
    ];

    return (
      <View ref={ref} style={cellStyle} {...props}>
        {typeof children === 'string' || typeof children === 'number' ? (
          <TextReg style={styles.cellText}>{children}</TextReg>
        ) : (
          children
        )}
      </View>
    );
  },
);

TableCell.displayName = 'TableCell';

const createStyles = ({
  isMobile,
  isTablet,
}: {
  isMobile: boolean;
  isTablet: boolean;
}) =>
  StyleSheet.create({
    cell: {
      paddingHorizontal: 16,
      paddingVertical: 14,
      justifyContent: 'center',
    },
    cellText: {
      fontSize: 14,
      color: Colors.text,
    },
    centerAlign: {
      alignItems: 'center',
    },
    rightAlign: {
      alignItems: 'flex-end',
    },
    hideOnMobile: {
      display: isMobile ? 'none' : 'flex',
    },
    hideOnTablet: {
      display: isTablet ? 'none' : 'flex',
    },
  });
